import { useLocalStorage } from "hooks/useLocalStorage";
import { createContext, useCallback, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { MyConst } from "data/MyConst";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useLocalStorage("user", null);
  const navigate = useNavigate();

  const login = useCallback(async (data) => {
    setUser(data);
    navigate("/", { replace: true });
  }, [navigate, setUser]);

  const replaceUser = useCallback(async (data) => {
    setUser(data);
  }, [setUser]);

  const switchContext = useCallback(async (data) => {
    setUser(data);
    navigate("/", { replace: true });
  }, [navigate, setUser]);

  const logout = useCallback(() => {
    setUser(null);
    navigate("/login", { replace: true });
  }, [navigate, setUser]);

  const isTokenExpired = useCallback(() => {
    if (user?.token) {
      const decoded = jwtDecode(user.token);
      return decoded.exp * 1000 < Date.now();
    }
    return false;
  }, [user?.token]);

  const isRefreshTokenExpired = useCallback(() => {
    if (user?.retoken) {
      const decoded = jwtDecode(user.retoken);
      return decoded.exp * 1000 < Date.now();
    }
    return false;
  }, [user?.retoken]);

  const isVersionObsolete = useCallback(() => {
    if (user) {
      return MyConst.VERSION !== user?.version;
    }
    return false;
  }, [user]);

  const value = useMemo(
    () => ({
      user,
      login,
      logout,
      switchContext,
      isTokenExpired,
      isRefreshTokenExpired,
      replaceUser,
      isVersionObsolete,
    }),
    [user, login, logout, switchContext, isTokenExpired, isRefreshTokenExpired, replaceUser, isVersionObsolete]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};
